import { DEMO_CLIENT_ID } from '../src/config';
import type { Setup, StepState } from './api';

/** +12486863933 → (248) 686-3933. Anything else is shown as it is. */
export function phone(e164?: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164 ?? '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : (e164 ?? '');
}

/** Millionths of a dollar → $27.40. A cost under a cent still shows as one. */
export function money(micros: number): string {
  const dollars = micros / 1e6;
  if (micros > 0 && dollars < 0.005) return '<$0.01';
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export const count = (n: number) => n.toLocaleString('en-US');

const sameYear = (d: Date) => d.getFullYear() === new Date().getFullYear();

/** Sep 2 (this year) or Sep 2, 2025. */
export function day(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear(d) ? {} : { year: 'numeric' }) });
}

/** Sep 2, 3:14 PM */
export function when(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${day(iso)}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** How long ago, in the words you'd use: Just now, 12 min ago, 3 hours ago, Yesterday, 6 days ago, Sep 2. */
export function ago(iso?: string, never = 'Never'): string {
  if (!iso) return never;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return never;
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const days = Math.round((startOfDay(new Date()) - startOfDay(new Date(t))) / 86_400_000);
  if (days <= 0) {
    const hours = Math.floor(mins / 60);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return day(iso);
}

export const plural = (n: number, one: string, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;

/** Trade and town, as one line: Roofing, Novi. */
export const tradeTown = (c: { industry?: string; city?: string }) => [c.industry, c.city].filter(Boolean).join(', ');

/** The setup steps, in the order they're usually done. */
export const STEPS: { key: keyof Setup; name: string }[] = [
  { key: 'number', name: 'Number' },
  { key: 'tenDlc', name: '10DLC' },
  { key: 'logins', name: 'Logins' },
  { key: 'calendar', name: 'Calendar' },
  { key: 'live', name: 'Live' },
];

export const STATE_WORD: Record<StepState, string> = { done: 'done', pending: 'under way', broken: 'stopped working', todo: 'not yet' };

export type Tone = 'good' | 'watch' | 'problem' | 'quiet';

/** Issues that need LeadsIRL. A switched-off shop is a state, not an issue: it has its own filter. */
export const issuesOf = (c: { attention: string[] }) => c.attention.filter((a) => a !== 'Switched off');
export const needsYou = (c: { active: boolean; attention: string[] }) => c.active && issuesOf(c).length > 0;

export const toneOfIssue = (issue: string): Tone => (issue.startsWith('Owner has not') ? 'watch' : 'problem');

/** The next setup step, as the shop's one-line status while it sets up. */
export function nextStep(s: Setup): string {
  if (s.number !== 'done') return 'Needs a business number';
  if (s.tenDlc === 'broken') return '10DLC campaign rejected';
  if (s.tenDlc === 'pending') return 'Waiting on 10DLC approval';
  if (s.tenDlc !== 'done') return '10DLC not submitted yet';
  if (s.logins === 'todo') return 'Owner not invited yet';
  if (s.logins !== 'done') return 'Owner invited, not signed in yet';
  if (s.calendar !== 'done') return 'Calendar not connected yet';
  return 'Ready to go live';
}

/** One line on where a shop stands, and its color. */
export function caption(c: { active: boolean; demo: boolean; liveAt?: string; setup: Setup; attention: string[] }): [Tone, string] {
  if (!c.active) return ['quiet', 'Switched off: calls and texts stopped'];
  const issues = issuesOf(c);
  if (issues.length) return [toneOfIssue(issues[0]), issues.length > 1 ? `${issues[0]}, and ${issues.length - 1} more` : issues[0]];
  if (c.setup.live === 'done') return ['good', c.demo ? 'Live, used for sales demos' : `Live since ${day(c.liveAt)}`];
  return ['watch', nextStep(c.setup)];
}

export const CRM_URL = 'https://crm.leadsirl.com';
/** A shop's own CRM, opened in support view. The demo shop is LeadsIRL's own, so no support view. */
export const crmLink = (id: string, demo = false) => (demo && id === DEMO_CLIENT_ID ? `${CRM_URL}/` : `${CRM_URL}/?shop=${encodeURIComponent(id)}`);
